import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/api';

export type AccessLevel = 'full' | 'read_only' | 'locked';

export interface TenantAccessInfo {
  level: AccessLevel;
  // Quando 'full' e ainda em trial: dias restantes (negativo se já passou de trial_ends_at mas o cron ainda não rodou).
  trialDaysLeft: number | null;
  // Quando em graça: dias restantes até o lock.
  graceDaysLeft: number | null;
  reason: string | null;
  rawStatus: string | null;
}

/**
 * Avalia o nível de acesso do tenant a partir do GET /api/tenant.
 * Espelha a lógica do TenantGateService no backend para que a UI
 * decida banner / paywall sem precisar fazer uma chamada extra.
 */
export function useTenantAccess(): { data: TenantAccessInfo | null; isLoading: boolean } {
  const { data: tenant, isLoading } = useQuery({
    queryKey: ['tenant'],
    queryFn: () => apiClient.getTenant().then(r => r.data),
    staleTime: 60 * 1000,
  });

  if (!tenant) return { data: null, isLoading };

  const now = Date.now();
  const trialEndsAt = tenant.trial_ends_at ? new Date(tenant.trial_ends_at).getTime() : null;
  const graceEndsAt = tenant.grace_ends_at ? new Date(tenant.grace_ends_at).getTime() : null;
  const status: string = tenant.status;
  const reason: string | null = tenant.status_reason ?? null;

  // trialDaysLeft só faz sentido quando o tenant ainda está em trial.
  // Se já está 'active' (pagou), trial_ends_at pode continuar preenchido no
  // banco mas não deve mais acionar o banner — a assinatura está ativa.
  const trialDaysLeft = status === 'trial' && trialEndsAt
    ? Math.ceil((trialEndsAt - now) / (24 * 3600 * 1000))
    : null;
  const graceDaysLeft = graceEndsAt
    ? Math.ceil((graceEndsAt - now) / (24 * 3600 * 1000))
    : null;

  let level: AccessLevel;
  if (status === 'active') {
    level = 'full';
  } else if (status === 'trial') {
    if (reason === 'trial_expired_grace') {
      level = graceEndsAt && graceEndsAt < now ? 'locked' : 'read_only';
    } else if (trialEndsAt && trialEndsAt < now) {
      level = 'read_only';
    } else {
      level = 'full';
    }
  } else if (status === 'past_due') {
    level = 'full';
  } else {
    level = 'locked';
  }

  return {
    data: { level, trialDaysLeft, graceDaysLeft, reason, rawStatus: status },
    isLoading,
  };
}
